/* eslint-disable no-nested-ternary */
import { Container } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Card from '@components/Card/Card';
import ContentLoader from '@components/ContentLoader';
import ViewTab from '@components/ViewTab';

import { useGetVitallPatientRecordMeta } from '@hooks/api/patient-record';
import { useSession } from '@hooks/useSession';

import { TableContainer } from '@layouts/Layouts/TableLayout';

import { CurationStatus } from 'generated/types';

import { CardContainer, RecordContainer } from './PatientRecords.style';
import InformationList from './RecordInformationList';

const messages = defineMessages({
  // Records
  newRecords: {
    id: 'clientProfile.newRecords',
    defaultMessage: 'New Records',
  },
  publishedRecords: {
    id: 'clientProfile.publishedRecords',
    defaultMessage: 'Published Records',
  },
  draftRecords: {
    id: 'clientProfile.draftRecords',
    defaultMessage: 'Draft Records',
  },
  needsReviewRecords: {
    id: 'clientProfile.needsReviewRecords',
    defaultMessage: 'Records Needing Review',
  },
});

const PatientRecords: React.FC = () => {
  const { formatMessage } = useIntl();
  const {
    state: { activeProfile },
  } = useSession();
  const [patient_id, user_id] = [
    activeProfile?.patient_id,
    activeProfile?.user_id,
  ];

  const { data: patientData, loading: patientLoading } =
    useGetVitallPatientRecordMeta({ user_id });

  const { palette } = useTheme();

  const tabs = [
    { id: 'newRecords', value: formatMessage(messages.newRecords) },
    {
      id: 'needsReviewRecords',
      value: formatMessage(messages.needsReviewRecords),
    },
    { id: 'draftRecords', value: formatMessage(messages.draftRecords) },
    { id: 'publishedRecords', value: formatMessage(messages.publishedRecords) },
  ];
  const [activeTab, setActiveTab] = useState(tabs[0]);

  const loading = patientLoading || !activeProfile;

  type DashboardCurationStatus =
    | CurationStatus.Draft
    | CurationStatus.NeedsReview
    | CurationStatus.New
    | CurationStatus.Published;
  const recordCounts: Record<DashboardCurationStatus, number> = {
    [CurationStatus.Draft]: patientData?.recordMeta?.Draft ?? 0,
    [CurationStatus.NeedsReview]: patientData?.recordMeta?.NeedsReview ?? 0,
    [CurationStatus.New]: patientData?.recordMeta?.New ?? 0,
    [CurationStatus.Published]: patientData?.recordMeta?.Published ?? 0,
  };

  const recordCards = [
    {
      number: recordCounts.New,
      text: formatMessage(messages.newRecords),
    },
    {
      number: recordCounts.NeedsReview,
      text: formatMessage(messages.needsReviewRecords),
    },
    {
      number: recordCounts.Draft,
      text: formatMessage(messages.draftRecords),
    },
    {
      number: recordCounts.Published,
      text: formatMessage(messages.publishedRecords),
    },
  ];

  if (!patient_id || !user_id) {
    return null;
  }

  return (
    <ContentLoader loading={loading}>
      <Container sx={{ py: 3, width: '100%' }}>
        <CardContainer>
          {recordCards.map(({ number, text }) => (
            <Card key={text}>
              <RecordContainer color={palette.primary.main} fontSize={28}>
                {number}
              </RecordContainer>
              <RecordContainer>{text}</RecordContainer>
            </Card>
          ))}
        </CardContainer>

        <ViewTab
          changeTab={setActiveTab}
          selected={activeTab}
          tabArray={tabs}
        />

        <TableContainer>
          <InformationList
            status={
              activeTab.id === 'newRecords'
                ? CurationStatus.New
                : activeTab.id === 'publishedRecords'
                  ? CurationStatus.Published
                  : activeTab.id === 'draftRecords'
                    ? CurationStatus.Draft
                    : CurationStatus.NeedsReview
            }
            type={activeTab.value.toUpperCase()}
          />
        </TableContainer>
      </Container>
    </ContentLoader>
  );
};

export default PatientRecords;
